import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { BlackjackGameState, BlackjackRoundState, BlackjackSplitPhase } from '@/types/blackjack'
import type { RootState } from '@/store/store'

function toSplitPhase(data: BlackjackRoundState): BlackjackSplitPhase {
	const hasSecondHand = data.playerHandTwo.length > 0
	if (!hasSecondHand) {
		return 'none'
	}

	return data.activeHandIndex === 0 ? 'firstHandActive' : 'secondHandActive'
}

export const initialBlackjackState: BlackjackGameState = {
	phase: 'ready',
	blackjackGameId: null,
	currencyCode: 'USD',
	betAmount: '1',
	dealerHand: [],
	playerHand: [],
	playerHandTwo: [],
	dealerValue: 0,
	playerValue: 0,
	playerValue2: 0,
	activeHandIndex: 0,
	splitPhase: 'none',
	canHit: false,
	canStand: false,
	canDouble: false,
	canSplit: false,
	canInsurance: false,
	status: 'idle',
	isSubmitting: false,
	isResultVisible: false,
	isClearing: false,
	currentMultiplier: 0,
	currentPayout: 0,
	currentProfit: 0,
	message: '',
}

const blackjackSlice = createSlice({
	name: 'blackjack',
	initialState: initialBlackjackState,
	reducers: {
		resetBlackjackGame(state) {
			return {
				...initialBlackjackState,
				currencyCode: state.currencyCode,
				betAmount: state.betAmount,
			}
		},
		setBlackjackCurrencyCode(state, action: PayloadAction<string>) {
			state.currencyCode = action.payload
		},
		setBlackjackBetAmount(state, action: PayloadAction<string>) {
			state.betAmount = action.payload
		},
		setBlackjackSubmitting(state, action: PayloadAction<boolean>) {
			state.isSubmitting = action.payload
		},
		setBlackjackClearing(state, action: PayloadAction<boolean>) {
			state.isClearing = action.payload
			if (action.payload) {
				state.isResultVisible = false
			}
		},
		clearBlackjackTable(state) {
			state.dealerHand = []
			state.playerHand = []
			state.playerHandTwo = []
			state.dealerValue = 0
			state.playerValue = 0
			state.playerValue2 = 0
			state.activeHandIndex = 0
			state.splitPhase = 'none'
			state.canHit = false
			state.canStand = false
			state.canDouble = false
			state.canSplit = false
			state.canInsurance = false
			state.status = 'idle'
			state.isResultVisible = false
			state.currentMultiplier = 0
			state.currentPayout = 0
			state.currentProfit = 0
			state.message = ''
		},
		applyBlackjackRoundState(state, action: PayloadAction<BlackjackRoundState>) {
			const data = action.payload

			state.dealerHand = data.dealerHand
			state.playerHand = data.playerHand
			state.playerHandTwo = data.playerHandTwo
			state.dealerValue = data.dealerValue
			state.playerValue = data.playerValue
			state.playerValue2 = data.playerValue2
			state.activeHandIndex = data.activeHandIndex
			state.splitPhase = toSplitPhase(data)
			state.canHit = data.canHit
			state.canStand = data.canStand
			state.canDouble = data.canDouble
			state.canSplit = data.canSplit
			state.canInsurance = data.canInsurance
			state.currentMultiplier = data.multiplier
			state.currentPayout = data.payout
			state.currentProfit = data.profit
			state.blackjackGameId = data.blackjackGame?.id ?? null
			state.message = data.message ?? ''
			state.status = data.status

			if (data.blackjackGame?.amount != null) {
				state.betAmount = String(data.blackjackGame.amount)
			}

			if (data.status === 'bet') {
				state.phase = 'live'
				state.isResultVisible = false
				return
			}

			if (data.status === 'insurance') {
				state.phase = 'insurance'
				state.isResultVisible = false
				return
			}

			if (data.status === 'idle') {
				state.phase = 'ready'
				state.blackjackGameId = null
				state.isResultVisible = false
				state.currentMultiplier = 0
				state.currentPayout = 0
				state.currentProfit = 0
				return
			}

			state.phase = 'ready'
			state.blackjackGameId = null
			state.canHit = false
			state.canStand = false
			state.canDouble = false
			state.canSplit = false
			state.canInsurance = false
			state.isResultVisible = false
		},
		showBlackjackResult(state) {
			state.isResultVisible = true
		},
		dismissBlackjackResult(state) {
			state.isResultVisible = false
			state.dealerHand = []
			state.playerHand = []
			state.playerHandTwo = []
			state.dealerValue = 0
			state.playerValue = 0
			state.playerValue2 = 0
			state.activeHandIndex = 0
			state.splitPhase = 'none'
			state.status = 'idle'
			state.currentMultiplier = 0
			state.currentPayout = 0
			state.currentProfit = 0
			state.message = ''
		},
	},
})

export const {
	resetBlackjackGame,
	setBlackjackCurrencyCode,
	setBlackjackBetAmount,
	setBlackjackSubmitting,
	setBlackjackClearing,
	clearBlackjackTable,
	applyBlackjackRoundState,
	showBlackjackResult,
	dismissBlackjackResult,
} = blackjackSlice.actions

export const selectBlackjackGame = (state: RootState) => state.blackjack

export default blackjackSlice.reducer
